/* eslint-disable no-unused-vars */
import { useDebounceCallback } from '@react-hook/debounce';
import { getAvailableMetrics } from 'common/services/metrics.service';
import { useEffect, useState } from 'react';
import useMetricsCalculationConfig from 'views/root/views/home/hooks/useMetricsCalculationConfig';
import { CUSTOM_DASHBOARD_INTERNAL_TYPES } from '@linearb/linjs-common';
import { organizationSelector } from 'state/selectors/organization';
import { useSelector } from 'react-redux';
import { myAccountSelector } from 'state/selectors/account';
import useSettingsService from 'components/hooks/useSettingsService';
import buildSearchOptionsForGraph from '../../Widgets/utils/buildSearchOptionsForWidget';
import useSearchRanges from '../../Widgets/CustomWidget/useSearchRanges';
import useMetricsV3Data from '../../Widgets/useMetricsV3Data';
import {
  formatWidgetsToCustomMetrics,
  formatWidgetsToReportedMetrics,
  formatWidgetsToV3Metrics,
  getGroups,
  groupData
} from './utils';
import { isFilteredByLabel } from '../../custom-report.util';
import { formatReportedMetricsResToCustomPmMetrics } from '../../Widgets/utils/formatters';
import { SEARCH_DATE_FORMAT } from 'common/constants/constants';
import moment from 'moment';
import { REPORT_IDS } from '../../consts';

const noTotalsNeededByInternalType = [
  CUSTOM_DASHBOARD_INTERNAL_TYPES.CUSTOM_PM_METRICS,
  CUSTOM_DASHBOARD_INTERNAL_TYPES.REPORTED_METRICS
];

const getIsTotalsNeeded = report => {
  if (report?.no_total_needed) {
    return false;
  }

  return !noTotalsNeededByInternalType.includes(report?.internal_type);
};

const useCustomDashboardMetrics = ({
  filters,
  report,
  reportDefinition,
  iterations = [],
  isInspection = false
}) => {
  const organization = useSelector(organizationSelector);
  const filteredByLabel = isFilteredByLabel({
    filters,
    isInspection,
    type: report?.internal_type
  });
  const account = useSelector(myAccountSelector) || {};
  const { fetchNewV3MetricsDataByFilter, exportMetricsV3 } = useMetricsV3Data();

  const isAICodeReviewReport = report.id === 'AI Code Review';

  const { getSettingOrDefinitionValue } = useSettingsService();
  const { timeRange } = useSearchRanges({ filters, iterations });

  const [metrics, setMetrics] = useState([]);
  const [metricsTotal, setMetricsTotal] = useState([]);
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [showAICodeReviewEmptyState, setShowAICodeReviewEmptyState] =
    useState(false);

  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const metricsCalculationConfig = useMetricsCalculationConfig();

  const isCustomPMMetrics =
    report?.internal_type === CUSTOM_DASHBOARD_INTERNAL_TYPES.CUSTOM_PM_METRICS;

  const widgets = report?.definition?.widgets ?? [];
  const { metricsCalculationStrategy } = metricsCalculationConfig;

  const isTotalsNeeded = getIsTotalsNeeded(report);

  const fetchReportedMetricsData = async () => {
    const reportedMetrics = formatWidgetsToReportedMetrics({
      isCustomPMMetrics,
      widgets
    });
    if (reportedMetrics.length) {
      const { metricsOptions: reportedMetricsOptions, averageOptions } =
        buildSearchOptionsForGraph({
          reportDefinition,
          filters: {
            ...filters,
            searchRange: timeRange
          },
          ...(reportedMetrics.length ? { reportedMetrics } : {})
        });

      const reportedMetricsDataByFilter = await fetchNewV3MetricsDataByFilter({
        metricsOptions: reportedMetricsOptions,
        averageOptions,
        isTotalsNeeded,
        filteredByLabel
      });
      const formattedResponse = formatReportedMetricsResToCustomPmMetrics(
        reportedMetricsDataByFilter?.data || []
      );
      return formattedResponse;
    }
    return [];
  };

  const checkIfAICodeReviewEmpty = async () => {
    if (!isAICodeReviewReport) {
      return false;
    }

    const today = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(today.getMonth() - 6);

    const metricsOptions = {
      time_ranges: [
        {
          before: moment(today).format(SEARCH_DATE_FORMAT),
          after: moment(sixMonthsAgo).format(SEARCH_DATE_FORMAT)
        }
      ],
      group_by: 'organization',
      roll_up: 'custom',
      is_reported_metrics: true,
      requested_metrics: [
        {
          name: 'gitstream.ai_review.issues',
          agg: 'count',
          filters: [
            {
              column: 'metric',
              type: 'equals',
              value: 'gitstream.ai_review.issues'
            }
          ]
        }
      ]
    };
    const AICodeReviewMetrics = await fetchNewV3MetricsDataByFilter({
      metricsOptions,
      averageOptions: {},
      isTotalsNeeded: false,
      filteredByLabel: false
    });

    const AICodeReviewCount =
      AICodeReviewMetrics.data?.[0]?.data?.[0]?.[
        'gitstream.ai_review.issues:count'
      ] || 0;

    return AICodeReviewCount === 0;
  };

  const fetchNewV3MetricsData = async gitScopeToggle => {
    const isAICodeReviewEmpty = await checkIfAICodeReviewEmpty();

    if (isAICodeReviewEmpty) {
      return { data: [], totals: [], isEmpty: true };
    }

    const v3BasedRequestedMetrics = formatWidgetsToV3Metrics({
      widgets,
      account,
      report,
      metricsCalculationStrategy,
      isCustomPMMetrics,
      getSettingOrDefinitionValue
    });

    const customMetrics = formatWidgetsToCustomMetrics({
      isCustomPMMetrics,
      widgets
    });

    const { metricsOptions, averageOptions } = buildSearchOptionsForGraph({
      reportDefinition,
      metrics: v3BasedRequestedMetrics,
      filters: {
        ...filters,
        searchRange: timeRange
      },
      gitScopeToggle,
      ...(customMetrics.length ? { customMetrics } : {})
    });

    const newV3MetricsDataByFilter = await fetchNewV3MetricsDataByFilter({
      metricsOptions,
      averageOptions,
      isTotalsNeeded,
      filteredByLabel
    });

    return newV3MetricsDataByFilter;
  };

  const fetchNewMetricsData = useDebounceCallback(async gitScopeToggle => {
    setLoadingMetrics(true);

    const { data, totals, isEmpty } = await fetchNewV3MetricsData(
      gitScopeToggle
    );
    const reportedMetricsData = await fetchReportedMetricsData();

    setMetricsTotal(totals);
    setMetrics([...data, ...reportedMetricsData]);
    setShowAICodeReviewEmptyState(isEmpty);
    setLoadingMetrics(false);
  }, 500);

  useEffect(() => {
    const fetchMetrics = async () => {
      const metricsData = await getAvailableMetrics();
      setAvailableMetrics(metricsData ? Object.values(metricsData) : []);
    };
    fetchMetrics();
  }, []);

  const exportMetricsV3Data = async (fileFormat, fileName) => {
    const { metricsCalculationStrategy } = metricsCalculationConfig;

    const v3BasedRequestedMetrics = formatWidgetsToV3Metrics({
      widgets,
      account,
      report,
      metricsCalculationStrategy,
      isCustomPMMetrics,
      getSettingOrDefinitionValue
    });

    const customMetrics = formatWidgetsToCustomMetrics({
      isCustomPMMetrics,
      widgets
    });

    const { metricsOptions } = buildSearchOptionsForGraph({
      reportDefinition,
      metrics: v3BasedRequestedMetrics,
      filters: {
        ...filters,
        searchRange: timeRange
      },
      ...(customMetrics.length ? { customMetrics } : {})
    });

    const newV3MetricsDataByFilter = await exportMetricsV3({
      ...metricsOptions,
      file_format: fileFormat,
      file_name: fileName,
      beautified: true,
      // this handling is temp until the ui supports the new implementation
      ...(filters?.selectedLabels?.length ? { group_by: 'label' } : {})
    });

    return newV3MetricsDataByFilter;
  };

  return {
    metrics,
    metricsTotal,
    availableMetrics,
    loadingMetrics,
    timeRange,
    fetchNewMetricsData,
    exportMetricsV3Data,
    setLoadingMetrics,
    showAICodeReviewEmptyState
  };
};

export default useCustomDashboardMetrics;
